import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { mediaUrl } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { Input } from "../components/ui/input";
import { Search, BookOpen } from "lucide-react";

function formatInline(text) {
  if (!text) return "";
  return text
    .replace(/\u00a0/g, " ")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>");
}

export default function Blog() {
  const { t, lang } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/blog?lang=${lang}`).then((r) => setPosts(r.data)).finally(() => setLoading(false));
  }, [lang]);

  const filtered = posts.filter((p) =>
    (p.title + p.excerpt + p.category).toLowerCase().includes(q.toLowerCase())
  );
  const [featured, ...rest] = filtered;

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-teal-700 mb-3">{t("blog.kicker")}</div>
          <h1 className="font-serif font-bold text-4xl sm:text-5xl tracking-tight text-slate-900">{t("blog.title")}</h1>
          <p className="mt-3 text-slate-600 max-w-lg">{t("blog.subtitle")}</p>
        </div>
        <div className="relative w-full sm:w-64">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="blog-search" placeholder={t("blog.search")} value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-11" />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-400 font-mono text-sm">{t("blog.loading")}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-500">{t("blog.none")}</div>
      ) : (
        <>
          {featured && (
            <Link to={`/blog/${featured.slug}`} data-testid={`blog-featured-${featured.slug}`} className="group grid md:grid-cols-2 gap-8 bg-white border border-slate-200 rounded-2xl overflow-hidden mb-10 hover:shadow-md transition-shadow">
              <div className="aspect-[16/10] md:aspect-auto overflow-hidden bg-slate-100">
                {featured.cover_image ? (
                  <img src={mediaUrl(featured.cover_image)} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200" />}
              </div>
              <div className="p-8 flex flex-col justify-center">
                <span className="font-mono text-[10px] uppercase tracking-widest text-teal-700">{featured.category} · {t("blog.featured")}</span>
                <h2 className="mt-3 font-serif font-bold text-2xl lg:text-3xl text-slate-900 leading-tight group-hover:text-teal-700 transition-colors">{featured.title}</h2>
                <p className="mt-4 text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: formatInline(featured.excerpt) }} />
                <span className="mt-5 inline-flex items-center gap-2 text-teal-700 font-medium">{t("blog.read")}</span>
              </div>
            </Link>
          )}

          <div className="grid md:grid-cols-3 gap-6">
            {rest.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group block bg-white border border-slate-200 rounded-xl overflow-hidden h-full hover:-translate-y-0.5 hover:shadow-md transition-all">
                  <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                    {p.cover_image ? (
                      <img src={mediaUrl(p.cover_image)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200" />}
                  </div>
                  <div className="p-5">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-teal-700">{p.category}</span>
                    <h3 className="mt-2 font-serif font-bold text-lg text-slate-900 leading-snug group-hover:text-teal-700 transition-colors">{p.title}</h3>
                    <p className="mt-2 text-sm text-slate-500 line-clamp-3" dangerouslySetInnerHTML={{ __html: formatInline(p.excerpt) }} />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
